/**
 * ============================================================================
 * tRPC Client - Supabase Edition
 * ============================================================================
 * 
 * This file provides the tRPC client configured for Supabase authentication.
 * 
 * Status: ✅ SUPABASE-READY
 * Replaces: Previous tRPC client (Manus OAuth)
 * 
 * Features:
 * - Automatic JWT token injection from Supabase session
 * - Type-safe API calls with AppRouterSupabase
 * - Error handling and retry logic
 * 
 * ============================================================================
 */

import { createTRPCReact, httpBatchLink } from '@trpc/react-query';
import type { AppRouterSupabase } from '../../../server/routers-supabase';
import superjson from 'superjson';

// Create tRPC React hooks
export const trpc = createTRPCReact<AppRouterSupabase>();

// Get API URL from environment
const getApiUrl = () => {
  // Use VITE_API_BASE_URL if defined (production override)
  if (import.meta.env.VITE_API_BASE_URL) {
    return `${import.meta.env.VITE_API_BASE_URL}/api/trpc`;
  }
  
  // Fallback to same origin (works for both dev and prod)
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api/trpc`;
  }
  
  // SSR fallback
  return '/api/trpc';
};

/**
 * Custom fetch that automatically adds Supabase JWT token
 * This is more reliable than using headers() callback
 */
function customFetch(url: RequestInfo | URL, options?: RequestInit): Promise<Response> {
  // Get token from localStorage
  const storageKey = 'sb-rirfypidnzsniygiksvg-auth-token';
  const sessionStr = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
  
  // Parse session and extract access token
  let authHeader = {};
  if (sessionStr) {
    try {
      const session = JSON.parse(sessionStr);
      if (session?.access_token) {
        authHeader = {
          Authorization: `Bearer ${session.access_token}`,
        };
      }
    } catch (error) {
      console.error('[tRPC] Failed to parse session:', error);
    }
  }
  
  // Merge auth header with existing headers
  // DO NOT override Content-Type - let tRPC/superjson set it
  const existingHeaders = options?.headers as Record<string, string> | undefined;
  const hasContentType = existingHeaders?.['Content-Type'] || existingHeaders?.['content-type'];
  
  const mergedOptions: RequestInit = {
    ...options,
    credentials: 'include',
    headers: {
      ...options?.headers,
      ...authHeader,
      // Only set Content-Type if not already set by tRPC
      ...(!hasContentType ? { 'Content-Type': 'application/json' } : {}),
    },
  };
  
  return fetch(url, mergedOptions);
}

/**
 * Create tRPC client with Supabase authentication
 */
export function createTRPCClient() {
  return trpc.createClient({
    links: [
      httpBatchLink({
        url: getApiUrl(),
        transformer: superjson,
        
        // Use custom fetch that injects the token
        fetch: customFetch,
      }),
    ],
  });
}

